import Groq from "groq-sdk";
import { GROQ_MODELS } from "./groq-client";
import type { DocumentField } from "../config/document-types";
import type { CountryConfig } from "../config/countries";
import {
  getDocumentRequirements,
  type DocumentRequirements,
} from "../db/queries/documentRequirements";

const groq = new Groq({
  apiKey: import.meta.env.VITE_GROQ_API_KEY,
  dangerouslyAllowBrowser: true,
});

// Cache para evitar regenerar los mismos formularios
const formFieldsCache = new Map<
  string,
  { fields: DocumentField[]; timestamp: number }
>();
const CACHE_DURATION = 4 * 24 * 60 * 60 * 1000; // 4 días

export interface GenerateFieldsParams {
  documentType: string;
  documentTypeName: string;
  documentTypeDescription?: string;
  country: CountryConfig;
  language: "es" | "en";
}

export interface GenerateFieldsResult {
  fields: DocumentField[];
  requirements: DocumentRequirements | null;
  metadata: {
    generated: boolean;
    cached: boolean;
    country: string;
    documentType: string;
    hasLegalFramework: boolean;
  };
}

/**
 * Genera campos de formulario dinámicamente usando AI
 * basándose en el tipo de documento y el país seleccionado
 * Enriquecido con document_requirements de la base de datos
 */
export async function generateDocumentFields(
  params: GenerateFieldsParams
): Promise<GenerateFieldsResult> {
  const {
    documentType,
    documentTypeName,
    documentTypeDescription,
    country,
    language,
  } = params;

  // These are used in cache key and prompt generation
  void documentTypeName; // Used in createFieldGenerationPrompt
  void documentTypeDescription; // May be undefined, used in prompt fallback

  // Generar clave de cache
  const cacheKey = `${documentType}-${country.code}-${language}`;

  // Verificar cache
  const cached = formFieldsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    // Load requirements even if cached for document generation later
    const requirements = await getDocumentRequirements(
      country.code,
      documentType
    );
    return {
      fields: cached.fields,
      requirements,
      metadata: {
        generated: true,
        cached: true,
        country: country.code,
        documentType,
        hasLegalFramework: requirements !== null,
      },
    };
  }

  // Cargar document_requirements de la base de datos
  const requirements = await getDocumentRequirements(
    country.code,
    documentType
  );

  try {
    const prompt = createFieldGenerationPrompt(params, requirements);

    const completion = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content:
            language === "es"
              ? "Eres un experto en derecho y generación de documentos legales. Tu tarea es analizar los requerimientos legales específicos de cada país y tipo de documento para generar formularios precisos y completos. Usarás la información legal proporcionada de la base de datos para asegurar cumplimiento total."
              : "You are an expert in law and legal document generation. Your task is to analyze the specific legal requirements of each country and document type to generate accurate and complete forms. You will use the legal information provided from the database to ensure full compliance.",
        },
        {
          role: "user",
          content: prompt,
        },
      ],
      model: GROQ_MODELS.DOCUMENTS,
      temperature: 0.3,
      max_tokens: 4000,
      response_format: { type: "json_object" },
    });

    const response = completion.choices[0]?.message?.content;
    if (!response) {
      throw new Error("No response from AI");
    }

    const parsedResponse = JSON.parse(response);
    const fields = parseAIFieldsResponse(parsedResponse);

    // Guardar en cache
    formFieldsCache.set(cacheKey, {
      fields,
      timestamp: Date.now(),
    });

    return {
      fields,
      requirements,
      metadata: {
        generated: true,
        cached: false,
        country: country.code,
        documentType,
        hasLegalFramework: requirements !== null,
      },
    };
  } catch (error) {
    console.error("Error generating document fields:", error);
    // En caso de error, retornar campos básicos mínimos
    return {
      fields: getDefaultFields(documentType, language),
      requirements,
      metadata: {
        generated: false,
        cached: false,
        country: country.code,
        documentType,
        hasLegalFramework: requirements !== null,
      },
    };
  }
}

/**
 * Crea el prompt para la AI con toda la información del país y tipo de documento
 * ENRIQUECIDO con document_requirements de la base de datos
 */
function createFieldGenerationPrompt(
  params: GenerateFieldsParams,
  requirements: DocumentRequirements | null
): string {
  const { documentTypeName, documentTypeDescription, country, language } =
    params;

  const isSpanish = language === "es";
  const legalSystem = isSpanish ? country.legalSystemEs : country.legalSystemEn;
  const currencyName = isSpanish
    ? country.currencyNameEs
    : country.currencyNameEn;
  const countryName = isSpanish ? country.nameEs : country.nameEn;

  // Si tenemos requirements de la DB, usarlos
  let legalContext = "";
  if (requirements) {
    const clauses = isSpanish
      ? requirements.requiredClauses.es
      : requirements.requiredClauses.en;
    const formatDesc = isSpanish
      ? requirements.formatDescriptionEs
      : requirements.formatDescriptionEn;

    legalContext = isSpanish
      ? `
**🔴 INFORMACIÓN LEGAL OBLIGATORIA DE LA BASE DE DATOS (DEBE USARSE):**

**Cláusulas Requeridas por Ley:**
${clauses.map((c, i) => `${i + 1}. ${c}`).join("\n")}

**Marcos Legales Aplicables:**
${requirements.legalFrameworkCodes.join(", ")}

**Formato Requerido:**
${formatDesc}

${requirements.notesEs ? `**Notas Importantes:** ${requirements.notesEs}` : ""}
`
      : `
**🔴 MANDATORY LEGAL INFORMATION FROM DATABASE (MUST BE USED):**

**Legally Required Clauses:**
${clauses.map((c, i) => `${i + 1}. ${c}`).join("\n")}

**Applicable Legal Frameworks:**
${requirements.legalFrameworkCodes.join(", ")}

**Required Format:**
${formatDesc}

${requirements.notesEn ? `**Important Notes:** ${requirements.notesEn}` : ""}
`;
  }

  return isSpanish
    ? `Analiza los requisitos legales para generar un formulario de "${documentTypeName}" en ${countryName}.

**Información del país:**
- País: ${countryName}
- Código: ${country.code}
- Sistema legal: ${legalSystem || "Civil Law"}
- Moneda: ${currencyName} (${country.currencyCode})
- Idioma principal: ${country.primaryLanguage}

**Tipo de documento:**
${documentTypeDescription || documentTypeName}
${legalContext}

**Tu tarea:**
Genera un array JSON de campos de formulario que:
1. Cubran TODAS las cláusulas requeridas listadas arriba
2. Sean específicos para ${countryName}
3. Incluyan campos para documentos de identidad del país (${country.code === "RD" ? "Cédula Dominicana" : "DNI/Cédula local"})
4. Usen la moneda correcta (${country.currencyCode})
5. Incluyan referencias a leyes locales cuando sea relevante

**Estructura de cada campo:**
{
  "key": "nombreCampo",
  "label": "Etiqueta clara en español",
  "placeholder": "Ejemplo útil",
  "type": "text|date|number|textarea|email|tel",
  "required": true|false,
  "group": "parties|dates|financial|terms|property|job|service|item|general",
  "helpText": "Por qué se necesita este campo (referencia legal si aplica)",
  "validation": {
    "pattern": "regex opcional",
    "min": número mínimo,
    "max": número máximo,
    "message": "Mensaje de error"
  }
}

Devuelve SOLO un objeto JSON con esta estructura:
{
  "fields": [array de campos ordenados lógicamente],
  "legalNotes": "Breve nota sobre consideraciones legales de ${countryName}"
}`
    : `Analyze the legal requirements to generate a form for "${documentTypeName}" in ${countryName}.

**Country information:**
- Country: ${countryName}
- Code: ${country.code}
- Legal system: ${legalSystem || "Civil Law"}
- Currency: ${currencyName} (${country.currencyCode})
- Primary language: ${country.primaryLanguage}

**Document type:**
${documentTypeDescription || documentTypeName}
${legalContext}

**Your task:**
Generate a JSON array of form fields that:
1. Cover ALL required clauses listed above
2. Are specific to ${countryName}
3. Include local ID document fields (${country.code === "RD" ? "Dominican Cédula" : "Local ID/Cédula"})
4. Use the correct currency (${country.currencyCode})
5. Include references to local laws when relevant

**Field structure:**
{
  "key": "fieldName",
  "label": "Clear label in English",
  "placeholder": "Helpful example",
  "type": "text|date|number|textarea|email|tel",
  "required": true|false,
  "group": "parties|dates|financial|terms|property|job|service|item|general",
  "helpText": "Why this field is needed (legal reference if applicable)",
  "validation": {
    "pattern": "optional regex",
    "min": minimum number,
    "max": maximum number,
    "message": "Error message"
  }
}

Return ONLY a JSON object with this structure:
{
  "fields": [array of logically ordered fields],
  "legalNotes": "Brief note on ${countryName} legal considerations"
}`;
}

/**
 * Parsea la respuesta de la AI y valida los campos
 */
function parseAIFieldsResponse(response: any): DocumentField[] {
  if (!response.fields || !Array.isArray(response.fields)) {
    throw new Error("Invalid AI response format");
  }

  return response.fields.map((field: any) => ({
    key: field.key || `field_${Date.now()}`,
    label: field.label || "Campo",
    placeholder: field.placeholder || "",
    type: field.type || "text",
    required: field.required !== false,
    group: field.group || "general",
    helpText: field.helpText,
    validation: field.validation,
  }));
}

/**
 * Campos por defecto en caso de que la AI falle
 */
function getDefaultFields(
  _documentType: string,
  language: "es" | "en"
): DocumentField[] {
  const isSpanish = language === "es";

  const baseFields: DocumentField[] = [
    {
      key: "party1Name",
      label: isSpanish ? "Nombre de la primera parte" : "First party name",
      placeholder: isSpanish ? "Nombre completo" : "Full name",
      type: "text",
      required: true,
      group: "parties",
    },
    {
      key: "party1Id",
      label: isSpanish
        ? "Documento de identidad (Primera parte)"
        : "ID Document (First party)",
      placeholder: isSpanish ? "Cédula o documento de identidad" : "ID number",
      type: "text",
      required: true,
      group: "parties",
    },
    {
      key: "party2Name",
      label: isSpanish ? "Nombre de la segunda parte" : "Second party name",
      placeholder: isSpanish ? "Nombre completo" : "Full name",
      type: "text",
      required: true,
      group: "parties",
    },
    {
      key: "party2Id",
      label: isSpanish
        ? "Documento de identidad (Segunda parte)"
        : "ID Document (Second party)",
      placeholder: isSpanish ? "Cédula o documento de identidad" : "ID number",
      type: "text",
      required: true,
      group: "parties",
    },
    {
      key: "date",
      label: isSpanish ? "Fecha" : "Date",
      placeholder: isSpanish ? "Fecha del documento" : "Document date",
      type: "date",
      required: true,
      group: "dates",
    },
    {
      key: "amount",
      label: isSpanish ? "Monto" : "Amount",
      placeholder: isSpanish
        ? "Monto en moneda local"
        : "Amount in local currency",
      type: "number",
      required: false,
      group: "financial",
    },
    {
      key: "details",
      label: isSpanish ? "Detalles adicionales" : "Additional details",
      placeholder: isSpanish
        ? "Información adicional relevante"
        : "Additional relevant information",
      type: "textarea",
      required: false,
      group: "general",
    },
  ];

  return baseFields;
}

/**
 * Limpia el cache (útil para testing o actualizaciones)
 */
export function clearFormFieldsCache(): void {
  formFieldsCache.clear();
}
